import escapeRegExp from 'lodash/escapeRegExp'
import map from 'lodash/map'
import reduce from 'lodash/reduce'
import { replaceTemplateTokens } from './templateTokens'

export interface TemplateArgs {
  text: string
  context: Context
}

export interface TemplateOptions extends TemplateArgs {
  /**
   * Wraps a fallback that stood in for a missing value, e.g. with `highlightTemplateFallback` from
   * `@paintscout/ui-business`. Presentations leave this out (#25242).
   */
  renderFallback?: (fallback: string) => string
}

export interface Context {
  [key: string]: ContextValue
}

export type ContextValue = string | (() => string) | number | (() => number)

/**
 * Replaces {{}} tags in the text using the values supplied in context.
 *
 * A `{{key|fallback}}` tag shows its fallback when the context value for `key` is empty. A key that
 * isn't in the context at all is left as written, the same as a plain `{{key}}`.
 */
export function template(args: TemplateOptions) {
  const { text, context, renderFallback } = args

  const contextValues = processContext(context)

  let compiledText = replaceTemplateTokens(text, ({ key, fallback }, match) => {
    // Plain `{{key}}` tags are handled by the loop below.
    if (!fallback || !(key in contextValues)) return match
    const value = contextValues[key]
    // The identity context (every value is its own `{{key}}`) is used while authoring — keep the
    // tag, fallback included, rather than collapsing it to a bare `{{key}}`.
    if (value === `{{${key}}}`) return match
    if (value || value === 0) return `${value}`
    return renderFallback ? renderFallback(fallback) : fallback
  })

  map(contextValues, (value, key) => {
    // eslint-disable-next-line security/detect-non-literal-regexp
    compiledText = compiledText.replaceAll(new RegExp(`{{${key}}}`, 'g'), value ? `${value}` : '')
  })

  // Preserves newlines
  return compiledText.replaceAll(/(\r\n|\n|\r)/g, '<br/>')
}

/**
 * The opposite of template, replaces values in the text with {{}} tags
 */
export function untemplate(args: TemplateArgs) {
  const { text, context } = args

  const contextValues = processContext(context)

  let compiledText = text

  map(contextValues, (value, key) => {
    const replace = value ? `{{${key}}}` : ''
    // eslint-disable-next-line security/detect-non-literal-regexp
    compiledText = compiledText.replaceAll(new RegExp(escapeRegExp(`${value}`), 'g'), replace)
  })

  return compiledText
}

/**
 * Enumerates through a Context object and runs any functional values to get the result.
 * If a functional value returns undefined or null, the value is omitted.
 */
function processContext(context: { [key: string]: ContextValue }) {
  return reduce(
    context,
    (values: { [key: string]: string | number }, contextValue, key) => {
      if (typeof contextValue === 'function') {
        const value = contextValue()
        if (value !== undefined || value !== null) {
          values[key] = value
        }
      } else {
        values[key] = contextValue
      }

      return values
    },
    {}
  )
}
